/**
 * SummarizeService
 *
 * Streams AI overview / per-listing summaries / "next queries" chips from the
 * appx-gen-ai-summary-service `/v1/summarize` SSE endpoint. The contract is
 * documented in
 *   appx-gen-ai-summary-service/docs/api-contract-summarize.md
 *
 * EventSource cannot be used because the endpoint is POST + JSON body; this
 * module uses fetch + a hand-rolled SSE block parser instead.
 */
import { GEN_AI_SUMMARY_SERVICE_URL } from '~/modules/config';
import { CORRELATION_ID_PARAM, randomUUID } from '~/modules/utilities/cross_env_utils';

export interface SummarizeListing {
    oafId: string;
    type: string;
    title: string;
    description: string;
    listingCategories: string[];
    publisher: string;
    averageRating?: number;
    reviewsAmount?: number;
}

export interface SummarizeSort {
    field: string;
    direction: 'Ascending' | 'Descending';
}

export interface SummarizeRequest {
    searchQuery: string;
    filters?: Record<string, string>;
    listings: SummarizeListing[];
    /** Omitted for the default ("relevant") sort — see searchSortOptionsHelper. */
    sort?: SummarizeSort;
}

export type SummarizeStreamEvent =
    | { event: 'ai_overview_chunk'; data: string }
    | { event: 'ai_overview_complete'; data: true }
    | { event: 'listing_summary_chunk'; data: { oafId: string; chunk: string } }
    | { event: 'listing_summary_complete'; data: { oafId: string } }
    | { event: 'next_queries'; data: string[] }
    | { event: 'done'; data: true }
    | { event: 'error'; data: { message: string } };

export class SummarizeHttpError extends Error {
    constructor(public readonly status: number) {
        super(`summarize failed: ${status}`);
        this.name = 'SummarizeHttpError';
    }
}

interface SummarizeOptions {
    signal?: AbortSignal;
    correlationId?: string;
    /** Override the endpoint origin (defaults to GEN_AI_SUMMARY_SERVICE_URL). Useful for tests. */
    endpoint?: string;
}

const SUMMARIZE_PATH = '/v1/summarize';

/**
 * Stream a summarize response. Yields one event per SSE block; the consumer is
 * responsible for terminal handling on `done` / `error`. The stream ends
 * naturally when the server closes the connection — callers should treat
 * "ended without `done` or `error`" as a soft truncation per §5.4 of the
 * contract.
 */
export async function* summarize(
    body: SummarizeRequest,
    opts: SummarizeOptions = {},
): AsyncGenerator<SummarizeStreamEvent, void, void> {
    const endpoint = opts.endpoint ?? GEN_AI_SUMMARY_SERVICE_URL;
    const url = `${endpoint}${SUMMARIZE_PATH}`;
    const correlationId = opts.correlationId ?? randomUUID();

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            accept: 'text/event-stream',
            [CORRELATION_ID_PARAM]: correlationId,
        },
        body: JSON.stringify(body),
        signal: opts.signal,
    });

    if (!res.ok || !res.body) {
        // 400 / 404 / 5xx — do NOT try to parse SSE
        throw new SummarizeHttpError(res.status);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // SSE chunks are inherently sequential — the decoder needs each
            // chunk before it can find the next event boundary, so awaiting
            // inside the loop is the correct pattern, not parallelizable work.
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read();
            if (done) {
                return;
            }
            buf += decoder.decode(value, { stream: true });

            // SSE event blocks are separated by a blank line ("\n\n"). Some
            // intermediaries normalize CRLF, so we tolerate either form.
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const sep = findBlockSeparator(buf);
                if (!sep) {
                    break;
                }
                const block = buf.slice(0, sep.index);
                buf = buf.slice(sep.index + sep.length);
                const parsed = parseEventBlock(block);
                if (parsed) {
                    yield parsed;
                }
            }
        }
    } finally {
        // Releasing the reader lets the underlying connection close when
        // the consumer aborts mid-stream.
        try {
            reader.releaseLock();
        } catch {
            // releaseLock throws if the reader is already in a closed/canceled
            // state; that's fine — the cleanup is best-effort.
        }
    }
}

/**
 * SSE block separator: blank line between blocks. Tolerate `\n\n` (spec) and
 * `\r\n\r\n` (CRLF-normalized intermediaries). Returns the offset of the
 * separator and its length, or `-1` when no complete block is buffered yet.
 */
type BlockSeparator = { index: number; length: number };

function findBlockSeparator(buf: string): BlockSeparator | null {
    const lf = buf.indexOf('\n\n');
    const crlf = buf.indexOf('\r\n\r\n');
    if (lf === -1 && crlf === -1) {
        return null;
    }
    if (lf === -1) {
        return { index: crlf, length: 4 };
    }
    if (crlf === -1) {
        return { index: lf, length: 2 };
    }
    return crlf < lf ? { index: crlf, length: 4 } : { index: lf, length: 2 };
}

function parseEventBlock(block: string): SummarizeStreamEvent | null {
    let event = '';
    // `data:` may be split across multiple lines per the SSE spec; we
    // concatenate them with `\n` before JSON parsing, matching browser
    // EventSource behavior.
    let dataRaw = '';
    let hasData = false;

    const lines = block.split(/\r?\n/);
    for (const line of lines) {
        if (line.startsWith(':')) {
            // SSE comment — used for keep-alives.
            continue;
        }
        if (line.startsWith('event:')) {
            event = line.slice(6).trimStart();
        } else if (line.startsWith('data:')) {
            dataRaw = hasData ? `${dataRaw}\n${line.slice(5).trimStart()}` : line.slice(5).trimStart();
            hasData = true;
        }
    }

    if (!event || !hasData) {
        return null;
    }

    let data: unknown;
    try {
        data = JSON.parse(dataRaw);
    } catch {
        // Malformed payload — drop the block rather than tearing down the
        // whole stream. The server is the source of truth; if a chunk is
        // unparseable the worst case is a small visible gap.
        return null;
    }

    return { event, data } as SummarizeStreamEvent;
}
